/**
 * @deprecated This component has moved to cinderblocks for shared use. Please use the cinderblocks version instead
 */
import React, { useMemo, useEffect, useReducer } from 'react';

import { Flex, Box, Button, Text, Card, Icon } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';

import { LAYOUT_MOBILE_FULL_BLEED_PROPS } from 'platform/components/layout/Layout';
import { getHubBuyingFeedRoute } from 'shared/helpers/routes';

import { PropertyCardProperty as Property } from 'buying/graphql/useProperties';
import { ExpandFeedCard } from 'buying/components/propertyCards/ExpandFeedCard';
import {
  MIN_PROPERTY_CARD_HEIGHT,
  MOBILE_PROPERTY_CARD_WIDTH,
  PropertyCard,
} from 'buying/components/propertyCards/PropertyCard';
import { PriceGuidancePropertyCard } from 'buying/components/propertyCards/PriceGuidancePropertyCard';
import { PropertyFiltersSummary } from 'buying/components/propertyCards/PropertyFiltersSummary';
import { PropertyPlaceholderCard } from 'buying/components/propertyCards/PropertyPlaceholderCard';
import { ViewMoreCard } from 'buying/components/propertyCards/ViewMoreCard';

import { PropertiesFilterFragment as PropertiesFilter } from 'buying/graphql/fragments/__generated__/PropertiesFilterFragment';
import { InternalLink } from 'platform/react-router';

export enum CardType {
  Property = 'PROPERTY',
  PriceGuidanceProperty = 'PRICE_GUIDANCE_PROPERTY',
}

export interface PropertyCard {
  type: CardType;
  property: Property;
}
export interface NonPropertyCard {
  type: 'VIEW_MORE' | 'PLACEHOLDER' | 'EXPAND_FEED';
}

type Card = PropertyCard | NonPropertyCard;

interface LoadingCardListProps {
  hasPropertyFilters?: boolean;
}

const LoadingCardList: React.FC<LoadingCardListProps> = ({ hasPropertyFilters }) => {
  const { isMobile } = useDevice();
  const cards = isMobile ? [1, 2] : [1, 2, 3];

  return (
    <Flex width="100%" flexDirection="column">
      {hasPropertyFilters && (
        <>
          <Box width={315} height={20} mb={5} backgroundColor="neutrals30" />
          <Box width={150} height={20} mb={5} backgroundColor="neutrals30" />
        </>
      )}
      <Flex gridGap={4} overflowX={isMobile ? 'scroll' : undefined}>
        {cards.map((idx) => (
          <Box
            key={`loading-card-${idx}`}
            data-testid={`loading-card`}
            flexGrow={1}
            flexShrink={isMobile ? 0 : undefined}
            width={MOBILE_PROPERTY_CARD_WIDTH}
            height={MIN_PROPERTY_CARD_HEIGHT}
            borderRadius="semiRounded"
            backgroundColor="neutrals20"
          />
        ))}
      </Flex>
    </Flex>
  );
};

interface DesktopCardListProps {
  properties: Property[];
  numCards: number;
  propertyFilters?: PropertiesFilter;
  enableCarousel?: boolean;
  enableViewMoreCard?: boolean;
  enableExpandFeedCard?: boolean;
  headerText?: string;
  newHomesCount?: number;
  popularPropertiesRedirect: boolean;
  feedPropertiesCount?: string;
  market?: string;
  hoveredPropertyId?: string | null;
  type?: CardType;
  onHover?: (e?: React.MouseEvent<HTMLDivElement> | null) => void;
  cardOnClick?: (index: number | null) => void;
  comparisonProperty?: Property | null;
}

const DesktopCardList: React.FC<DesktopCardListProps> = ({
  properties,
  numCards = 3,
  propertyFilters,
  enableCarousel,
  enableViewMoreCard,
  enableExpandFeedCard,
  headerText,
  newHomesCount,
  popularPropertiesRedirect,
  feedPropertiesCount,
  market,
  hoveredPropertyId,
  type,
  onHover,
  cardOnClick,
  comparisonProperty,
}) => {
  const [page, setPage] = useReducer((state: number, action: number) => {
    return state + action;
  }, 1);
  const carouselMaxCards = numCards * 2;

  const isLastPage = useMemo(() => {
    if (enableExpandFeedCard) {
      return properties.length < page * numCards;
    }
    // Limit carousel length to 8 cards
    if (page >= carouselMaxCards) {
      return true;
    }
    return properties.length <= page * numCards;
  }, [enableExpandFeedCard, page, carouselMaxCards, properties.length, numCards]);

  const cardsInThisPage: Card[] = useMemo(() => {
    // Slice properties to get only the ones needed for this page
    const cardsInThisPage: Card[] = properties
      .slice((page - 1) * numCards, page * numCards)
      .map((property) => {
        return { type: type ?? CardType.Property, property };
      });

    // If we haven't reached numCards yet, fill in the gap with placeholder cards
    while (cardsInThisPage.length < numCards) {
      cardsInThisPage.push({ type: 'PLACEHOLDER' });
    }

    // If we want a view more / expand feed card and are on the last page of the carousel, replace
    // the last card with a view more / expand feed one.
    if ((enableExpandFeedCard && isLastPage) || feedPropertiesCount === '0') {
      cardsInThisPage[cardsInThisPage.length - 1] = { type: 'EXPAND_FEED' };
    } else if (enableViewMoreCard && isLastPage) {
      cardsInThisPage[cardsInThisPage.length - 1] = { type: 'VIEW_MORE' };
    }

    return cardsInThisPage;
  }, [
    properties,
    numCards,
    enableExpandFeedCard,
    isLastPage,
    feedPropertiesCount,
    enableViewMoreCard,
    type,
    page,
  ]);

  const viewMoreCarouselCards: PropertyCard[] = useMemo(() => {
    const cardsInThisPage: PropertyCard[] = properties
      .slice(carouselMaxCards + 2, carouselMaxCards + 6)
      .map((property) => {
        return { type: CardType.Property, property };
      });

    return cardsInThisPage;
  }, [properties, carouselMaxCards]);

  const handleCarouselClick = (direction: 'left' | 'right') =>
    setPage(direction === 'left' ? -1 : 1);

  useEffect(() => {
    if (hoveredPropertyId) {
      const isInCurrentPage =
        hoveredPropertyId &&
        cardsInThisPage.find((card) => {
          return (
            card.type === CardType.PriceGuidanceProperty && card?.property?.id === hoveredPropertyId
          );
        });
      if (!isInCurrentPage) {
        const indexOfProperty = properties.findIndex(
          (property) => property.id === hoveredPropertyId,
        );
        setPage(Math.ceil((indexOfProperty + 1) / numCards) - page);
      }
    }
  }, [cardsInThisPage, hoveredPropertyId, numCards, page, properties]);

  return (
    <Flex width="100%" flexDirection="column" role="region" aria-label="Carousel of homes for sale">
      <Flex justifyContent="space-between" mt={5}>
        {propertyFilters && (
          <Flex flexDirection="column" alignItems="left" justifyContent="center">
            <Text size="s3" color="neutrals100" fontWeight="bold" alignSelf="">
              {headerText}
            </Text>

            <PropertyFiltersSummary propertyFilters={propertyFilters} />
          </Flex>
        )}
        {!propertyFilters && (
          <Text size="s2" color="neutrals100" fontWeight="semibold" alignSelf="center">
            {headerText}
          </Text>
        )}
        {enableCarousel && (
          <Flex my={4} data-testid="carousel-nav" id="carousel-nav">
            <Button
              variant="icon"
              mr={3}
              onClick={handleCarouselClick.bind(null, 'left')}
              disabled={page === 1}
              aria-label="View previous property card"
              analyticsName="cake-view-previous-property-card"
            >
              <Icon name="chevron-left" size={16} />
            </Button>
            <Button
              variant="icon"
              onClick={handleCarouselClick.bind(null, 'right')}
              disabled={isLastPage}
              aria-label="View next property card"
              analyticsName="cake-view-next-property-card"
            >
              <Icon name="chevron-right" size={16} />
            </Button>
          </Flex>
        )}
        {(newHomesCount || newHomesCount === 0) && propertyFilters && (
          <Flex my={6}>
            <InternalLink
              to={getHubBuyingFeedRoute()}
              analyticsName="see-all-homes"
              aria-label={null}
            >
              <Flex alignItems="center">
                <Text color="brand50" fontWeight="semibold">
                  See all {newHomesCount && newHomesCount > 1 ? newHomesCount : ''} homes
                </Text>
                <Icon name="chevron-right" color="brand50" size={16} />
              </Flex>
            </InternalLink>
          </Flex>
        )}
      </Flex>
      <Flex gridGap={4} mt="12px">
        {cardsInThisPage.map((card, index) => {
          switch (card.type) {
            case CardType.Property:
              return (
                <Box
                  flexBasis="100%"
                  key={`property-card-div-${index}-${card.property.id}`}
                  data-testid="property-card"
                  role="group"
                  aria-label="Home"
                >
                  <PropertyCard hoveredPropertyId={hoveredPropertyId} property={card.property} />
                </Box>
              );
            case CardType.PriceGuidanceProperty:
              return (
                <Box
                  flexBasis="100%"
                  key={`property-card-div-${index}-${card.property.id}`}
                  data-testid="property-card"
                  role="group"
                  aria-label="Home"
                >
                  <PriceGuidancePropertyCard
                    hoveredPropertyId={hoveredPropertyId}
                    property={card.property}
                    carouselDisabled={false}
                    onHover={onHover}
                    comparisonProperty={comparisonProperty}
                    onClick={() => cardOnClick && cardOnClick((page - 1) * numCards + index)}
                  />
                </Box>
              );
            case 'VIEW_MORE':
              return (
                <Box flexBasis="100%" key={`view-more-div-${index}`} data-testid="view-more">
                  <ViewMoreCard
                    popularPropertiesRedirect={popularPropertiesRedirect}
                    feedPropertiesCount={feedPropertiesCount}
                    market={market}
                    staticCarouselProperties={viewMoreCarouselCards}
                  />
                </Box>
              );
            case 'EXPAND_FEED':
              return (
                <Box flexBasis="100%" key={`expand-feed-div-${index}`} data-testid="expand-feed">
                  <ExpandFeedCard />
                </Box>
              );
            case 'PLACEHOLDER':
              return (
                <Box flexBasis="100%" key={`placeholder-div-${index}`}>
                  <PropertyPlaceholderCard index={index} />
                </Box>
              );
          }
        })}
      </Flex>
    </Flex>
  );
};

interface MobileCardListProps {
  propertyFilters?: PropertiesFilter;
  properties: Property[];
  enableViewMoreCard?: boolean;
  enableExpandFeedCard?: boolean;
  headerText?: string;
  popularPropertiesRedirect: boolean;
  feedPropertiesCount?: string;
  market?: string;
  hoveredPropertyId?: string | null;
  type?: CardType;
  onTouch?: (e?: React.TouchEvent<HTMLDivElement> | null) => void;
  cardOnClick?: (index: number | null) => void;
  comparisonProperty?: Property | null;
}

const MobileCardList: React.FC<MobileCardListProps> = ({
  properties,
  propertyFilters,
  enableViewMoreCard,
  enableExpandFeedCard,
  headerText,
  popularPropertiesRedirect,
  feedPropertiesCount,
  market,
  hoveredPropertyId,
  type,
  onTouch,
  cardOnClick,
  comparisonProperty,
}) => {
  const carouselMaxCards = 8;
  const viewMoreCarouselMaxCards = 4;
  const allCards: Card[] = useMemo(() => {
    const cardsInThisPage: Card[] = properties.slice(0, carouselMaxCards).map((property) => {
      return { type: type ?? CardType.Property, property };
    });

    if (enableExpandFeedCard || feedPropertiesCount === '0') {
      cardsInThisPage.push({ type: 'EXPAND_FEED' });
    } else if (enableViewMoreCard) {
      cardsInThisPage.push({ type: 'VIEW_MORE' });
    }

    return cardsInThisPage;
  }, [properties, enableExpandFeedCard, feedPropertiesCount, enableViewMoreCard, type]);

  const viewMoreCarouselCards: PropertyCard[] = useMemo(() => {
    const cards: PropertyCard[] = properties
      .slice(carouselMaxCards, carouselMaxCards + viewMoreCarouselMaxCards)
      .map((property) => {
        return { type: CardType.Property, property };
      });

    return cards;
  }, [properties]);

  useEffect(() => {
    if (hoveredPropertyId) {
      const element = document.getElementById(`price-guidance-property-card-${hoveredPropertyId}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  });

  return (
    <Flex flexDirection="column">
      <Flex flexDirection="column" maxWidth="90%">
        <Text
          as="h2"
          size="s2"
          color="neutrals100"
          fontWeight="semibold"
          alignSelf="flex-start"
          mt={5}
        >
          {headerText}
        </Text>
        {propertyFilters && <PropertyFiltersSummary propertyFilters={propertyFilters} />}
      </Flex>
      <Flex
        overflowX="scroll"
        gridGap={4}
        pb={6} // don't cut off drop shadow
        pt={6}
        {...LAYOUT_MOBILE_FULL_BLEED_PROPS}
      >
        {allCards.map((card, index) => {
          switch (card.type) {
            case CardType.Property:
              return (
                <Box flexShrink={0} key={card.property.id} data-testid="property-card">
                  <PropertyCard
                    hoveredPropertyId={hoveredPropertyId}
                    property={card.property}
                    carouselDisabled={true}
                  />
                </Box>
              );
            case CardType.PriceGuidanceProperty:
              return (
                <Box
                  flexShrink={0}
                  key={card.property.id}
                  data-testid="price-guidance-property-card"
                >
                  <PriceGuidancePropertyCard
                    hoveredPropertyId={hoveredPropertyId}
                    property={card.property}
                    carouselDisabled={true}
                    comparisonProperty={comparisonProperty}
                    onTouch={onTouch}
                    onClick={() => cardOnClick && cardOnClick(index)}
                  />
                </Box>
              );
            case 'VIEW_MORE':
              return (
                <Box flexShrink={0} key={`view-more-${index}`} data-testid="view-more">
                  <ViewMoreCard
                    popularPropertiesRedirect={popularPropertiesRedirect}
                    feedPropertiesCount={feedPropertiesCount}
                    market={market}
                    staticCarouselProperties={viewMoreCarouselCards}
                  />
                </Box>
              );
            case 'EXPAND_FEED':
              return (
                <Box flexShrink={0} key={`expand-feed-div-${index}`} data-testid="expand-feed">
                  <ExpandFeedCard />
                </Box>
              );
          }
        })}
      </Flex>
    </Flex>
  );
};

export interface PropertyCardRowProps {
  propertyFilters?: PropertiesFilter;
  numCards?: number;
  properties: Property[];
  enableCarousel?: boolean;
  enableViewMoreCard?: boolean;
  enableExpandFeedCard?: boolean;
  newHomesCount?: number;
  headerText?: string;
  headerTextMobile?: string;
  isLoading: boolean;
  feedPropertiesCount?: string;
  market?: string;
  hoveredPropertyId?: string | null;
  type?: CardType;
  onTouch?: (e?: React.TouchEvent<HTMLDivElement> | null) => void;
  onHover?: (e?: React.MouseEvent<HTMLDivElement> | null) => void;
  cardOnClick?: (index: number | null) => void;
  comparisonProperty?: Property | null;
}

/** A row of PropertyCards */
export const PropertyCardRow: React.FC<PropertyCardRowProps> = ({
  numCards = 3,
  properties,
  propertyFilters,
  enableCarousel,
  enableViewMoreCard,
  enableExpandFeedCard,
  newHomesCount,
  headerText,
  headerTextMobile,
  isLoading,
  feedPropertiesCount,
  market,
  hoveredPropertyId,
  type,
  onTouch,
  onHover,
  cardOnClick,
  comparisonProperty,
}) => {
  const { isMobile } = useDevice();
  const popularPropertiesRedirect = headerText === 'Popular homes within your search';

  if (isLoading) {
    return <LoadingCardList hasPropertyFilters={!!propertyFilters} />;
  } else if (isMobile) {
    return (
      <MobileCardList
        properties={properties}
        propertyFilters={propertyFilters}
        enableViewMoreCard={enableViewMoreCard}
        enableExpandFeedCard={enableExpandFeedCard}
        headerText={headerTextMobile}
        popularPropertiesRedirect={popularPropertiesRedirect}
        feedPropertiesCount={feedPropertiesCount}
        market={market}
        hoveredPropertyId={hoveredPropertyId}
        type={type}
        onTouch={onTouch}
        comparisonProperty={comparisonProperty}
        cardOnClick={cardOnClick}
      />
    );
  }

  return (
    <DesktopCardList
      properties={properties}
      numCards={numCards}
      propertyFilters={propertyFilters}
      enableCarousel={enableCarousel}
      enableViewMoreCard={enableViewMoreCard}
      enableExpandFeedCard={enableExpandFeedCard}
      headerText={headerText}
      newHomesCount={newHomesCount}
      popularPropertiesRedirect={popularPropertiesRedirect}
      feedPropertiesCount={feedPropertiesCount}
      market={market}
      hoveredPropertyId={hoveredPropertyId}
      type={type}
      onHover={onHover}
      comparisonProperty={comparisonProperty}
      cardOnClick={cardOnClick}
    />
  );
};
